import Group from "../classes/Group";
import { FabricPoint } from "../objects";

declare module "fabric" {
  interface ActiveSelection {
    groupObject?: Group;
    locked: boolean;
    setPosition: (
      key: "x" | "y" | { x: number; y: number },
      value?: number,
    ) => void;
  }

  interface Canvas {
    selectionMode: SelectionModeType;
    viewportPoint: FabricPoint;
    layerOrigin: LayerOrigin;
    backgroundImagePath?: string;
    libraryItemId?: string | null;
  }

  interface CanvasEvents {
    panning: { target: Canvas };
    "background:loaded": { target: Canvas };
    "clippath:modified": { target: Canvas };
    "group:created": { target: Group };
    "group:removed": { target: Group };
    "group:focus": { target?: Group };
  }

  interface IText {
    prefix: string;
    infix: string;
    suffix: string;
    fontPath: string;
    fontSrc: string;
    textCase: TextCaseType;
    maxFontSize: number;
    fontSizeScalable: boolean;
    startAngle: number;
    endAngle: number;
    patternImagePath: string;
    patternScale: number;
    patternFit: "line" | "cover";
    textDirection: TextDirectionType;
    warpStyle: WarpStyle;
    transformOptions?: WarpTransformOptions;
    warpPresetParams?: WarpPresetParams | null;
  }

  interface FabricImage {
    defaultSrc: string;
    originalSrc: Blob | string;
    filter: ImageFilterProps;
    strokeEffect: ImageStrokeEffectProps;
    objectFit: ObjectFitType;
    maskImagePath: string;
    maskImageSrc: string;
    libraryItemId: string | null;
  }
}

export enum ExportFormat {
  PNG = "png",
  JPG = "jpg",
  JPEG = "jpeg",
}

export enum SelectionModeType {
  Single = "single",
  Multiple = "multiple",
  Range = "range",
}

export type LayerOrigin = {
  originX: "left" | "center" | "right";
  originY: "top" | "center" | "bottom";
};

export enum LayerCustomType {
  Base = "base",
  Text = "text",
  CurvedText = "curved-text",
  WarpedText = "warped-text",
  Image = "image",
  CustomImage = "custom-image",
  ClipArt = "clip-art",
  QRCode = "qr-code",
  Calendar = "calendar",
  Clock = "clock",
  Crossword = "crossword",
  WordSearch = "word-search",
}

export enum WarpStyle {
  Arch = "arch",
  BowTie = "bow-tie",
  Bubble = "bubble",
  Wave = "wave",
  Rectangle = "rectangle",
}

export interface WarpTransformPoint {
  x: number;
  y: number;
}

export interface WarpTransformOptions {
  points: WarpTransformPoint[];
}

export interface WarpPresetParams {
  bend?: number; // 0 = flat (rectangle), 1 = full preset shape
}

export enum LayerAlignmentType {
  Left = "left",
  HorizontalCenter = "horizontal-center",
  Right = "right",
  Top = "top",
  VerticalCenter = "vertical-center",
  Bottom = "bottom",
  Center = "center",
  Fit = "fit",
}

export enum TextAlignmentType {
  Left = "left",
  Right = "right",
  Center = "center",
}

export enum TextCaseType {
  None = "",
  Uppercase = "uppercase",
  Lowercase = "lowercase",
  TitleCase = "title-case",
}

export enum TextDirectionType {
  Horizontal = "horizontal",
  Vertical = "vertical",
}

export enum ObjectFitType {
  Contain = "contain",
  ContainTop = "contain-top",
  ContainTopLeft = "contain-top-left",
  ContainTopRight = "contain-top-right",
  ContainLeft = "contain-left",
  ContainLeftTop = "contain-left-top",
  ContainLeftBottom = "contain-left-bottom",
  ContainRight = "contain-right",
  ContainRightTop = "contain-right-top",
  ContainRightBottom = "contain-right-bottom",
  ContainBottom = "contain-bottom",
  ContainBottomLeft = "contain-bottom-left",
  ContainBottomRight = "contain-bottom-right",
  Cover = "cover",
  CoverTop = "cover-top",
  CoverTopLeft = "cover-top-left",
  CoverTopRight = "cover-top-right",
  CoverLeft = "cover-left",
  CoverLeftTop = "cover-left-top",
  CoverLeftBottom = "cover-left-bottom",
  CoverRight = "cover-right",
  CoverRightTop = "cover-right-top",
  CoverRightBottom = "cover-right-bottom",
  CoverBottom = "cover-bottom",
  CoverBottomLeft = "cover-bottom-left",
  CoverBottomRight = "cover-bottom-right",
  Fill = "fill",
}

export enum ImageFilterType {
  None = "",
  Grayscale = "Grayscale",
  BlendColor = "Blend color",
}

export interface ImageFilterProps {
  type: ImageFilterType;
  color?: string;
}

export enum ImageStrokeEffectType {
  None = "",
  Outline = "Outline",
}

export interface ImageStrokeEffectProps {
  type: ImageStrokeEffectType;
  color: string;
  width: number;
}

export enum GlobalCompositeOperation {
  SourceOver = "source-over",
  SourceIn = "source-in",
  SourceOut = "source-out",
  SourceAtop = "source-atop",
  DestinationOver = "destination-over",
  DestinationIn = "destination-in",
  DestinationOut = "destination-out",
  DestinationAtop = "destination-atop",
  Lighter = "lighter",
  Copy = "copy",
  Xor = "xor",
  Multiply = "multiply",
  Screen = "screen",
  Overlay = "overlay",
  Darken = "darken",
  Lighten = "lighten",
  ColorDodge = "color-dodge",
  ColorBurn = "color-burn",
  HardLight = "hard-light",
  SoftLight = "soft-light",
  Difference = "difference",
  Exclusion = "exclusion",
  Hue = "hue",
  Saturation = "saturation",
  Color = "color",
  Luminosity = "luminosity",
}
